/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Box, Divider, Flex, Heading, Input, Link, List, ListItem, Text, Textarea } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { DetailSheet } from '../../../components/DetailSheet'
import { FormField } from '../../../components/FormField'
import { FormModal } from '../../../components/FormModal'
import { Icon } from '../../../components/Icon'
import PreviewModal from '../../../components/previewModal'
import { Tooltip } from '../../../components/Tooltip'
import { useAppToast } from '../../../helpers'
import { EntregaDeTrabalhoService } from '../../../services/EntregaDeTrabalho/EntregaDeTrabalhoService'
import { courseFormat, downloadLink, previewLink } from './helper'
import { IEntregaDeTrabalhoComplete, IEntregaDeTrabalhoUpload, viewableFormats } from './models'

interface IProps {
  isOpen: boolean
  onClose: () => void
  id?: number
}

const defaultFormValues = {
  professorName: '',
  grade: '',
  observations: ''
}

export function EntregaDeTrabalhoReviewModal({ isOpen, onClose, id }: IProps) {
  const service = new EntregaDeTrabalhoService()
  const toast = useAppToast()
  const [workSubmission, setWorkSubmission] = useState<IEntregaDeTrabalhoComplete | null>(null)
  const [form, setForm] = useState(defaultFormValues)
  const [previewModal, setPreviewModal] = useState(false)
  const [previewData, setPreviewData] = useState<{ url: string; fileName: string; fileSize: number }>({
    url: '',
    fileName: '',
    fileSize: 0
  })

  useEffect(() => {
    setForm(defaultFormValues)
    loadWorkSubmission()
  }, [id])

  const loadWorkSubmission = async () => {
    if (!id) {
      return
    }

    setWorkSubmission(await service.get(id))
  }

  const handleGrade = (e: React.FocusEvent<HTMLInputElement>) => {
    let value = parseFloat(e.target.value)
    if (value < 0) {
      value = 0
    } else if (value > 10) {
      value = 10
    }

    const newValue = Math.round(value * 20) / 20

    setForm({ ...form, grade: newValue.toString() })
  }

  const handleOpenPreview = (upload: IEntregaDeTrabalhoUpload) => {
    setPreviewData({
      fileName: upload.original_name,
      url: previewLink(upload),
      fileSize: Number(upload.size)
    })

    setPreviewModal(true)
  }

  const handleSubmit = async () => {
    const allFieldsAreFilled = Object.values(form).every((value) => value.trim() !== '')

    if (!allFieldsAreFilled) {
      toast.warning('Campos obrigatórios', 'Preencha todos os campos para submeter a revisão.')
      return
    }

    if (isNaN(Number(form.grade))) {
      toast.warning('Nota inválida', 'A nota deve ser um número válido.')
      return
    }

    await service.review(workSubmission!.public_id, {
      teacher: form.professorName,
      observations: form.observations,
      grade: form.grade
    })

    toast.success('Trabalho revisado', 'A revisão do trabalho foi submetida')
    onClose()
  }

  return (
    workSubmission && (
      <>
        <FormModal
          isOpen={isOpen}
          onClose={onClose}
          title="Ficha de Término"
          onSubmit={handleSubmit}
          size="wide"
          confirmLabel="Validar Ficha"
          cancelLabel="Fechar"
        >
          <DetailSheet
            layout="grid"
            fields={[
              { label: 'Nome do Aluno', value: workSubmission.name },
              { label: 'CPF', value: workSubmission.cpf },
              { label: 'Curso', value: workSubmission.course },
              {
                label: 'Formato',
                value: courseFormat[workSubmission.format as 'presencial' | 'online' | 'online-ao-vivo']
              }
            ]}
          />
          <Divider my={4} />
          <FormField label="Nome do Professor">
            <Input
              size="sm"
              value={form.professorName}
              onChange={(e) => setForm({ ...form, professorName: e.target.value })}
            />
          </FormField>
          <FormField label="Nota" mt={2}>
            <Input
              size="sm"
              type="number"
              step="0.05"
              value={form.grade}
              onChange={(e) => setForm({ ...form, grade: e.target.value })}
              onBlur={handleGrade}
            />
          </FormField>
          <FormField label="Observações" mt={2}>
            <Textarea
              size="sm"
              value={form.observations}
              onChange={(e) => setForm({ ...form, observations: e.target.value })}
            />
          </FormField>
          <Box mx="auto" w={{ base: '100%', md: '66%' }}>
            <Heading mt={4} mb={2}>
              Arquivos enviados
            </Heading>
            <List>
              {workSubmission.uploads.map((upload) => (
                <ListItem key={upload.id}>
                  <Flex mb={2} gap={3} align="center">
                    <Box flex="1">
                      {upload.original_name}{' '}
                      <Text variant="caption" as="span">
                        ({(Number(upload.size) / 1024 / 1024).toFixed(2)} MB)
                      </Text>
                    </Box>
                    {viewableFormats.includes(upload.original_name.split('.').pop()?.toLowerCase() || '') ? (
                      <Tooltip label="Abrir preview">
                        <Link color={'blue.500'} onClick={() => handleOpenPreview(upload)}>
                          <Icon name="lni lni-eye" label="Abrir preview" />
                        </Link>
                      </Tooltip>
                    ) : (
                      <Tooltip label="Formato não suportado para visualização">
                        <Link color="blue.500" style={{ opacity: 0.4 }}>
                          <Icon name="lni lni-eye" label="Formato não suportado para visualização" />
                        </Link>
                      </Tooltip>
                    )}
                    <Tooltip label="Fazer download">
                      <Link target="_blank" href={downloadLink(upload)} rel="noreferrer" color={'blue.500'}>
                        <Icon name="lni lni-download" label="Fazer download" />
                      </Link>
                    </Tooltip>
                  </Flex>
                </ListItem>
              ))}
            </List>
          </Box>
        </FormModal>

        <PreviewModal
          isOpen={previewModal}
          onClose={() => {
            setPreviewModal(false)
          }}
          url={previewData.url}
          fileName={previewData.fileName}
          fileSize={previewData.fileSize}
        />
      </>
    )
  )
}
